import { useMutation } from '@tanstack/react-query'
import { LucideChevronDown, LucideSquareMousePointer } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LoadingCircle } from '~/components'
import { Accordion, Button, Dialog, Popover } from '~/components/ui'
import { useCRSListingLink } from '~/hooks/useCRSLink'
import { camelCaseToWords, type Listing } from '~/models'
import { cn, fmtAddress } from '~/utils'

export default function CrsData({ listing }: { listing: Listing }) {
  const links = useCRSListingLink(listing)

  return (
    <Popover>
      <Popover.Trigger asChild>
        <Button variant="outline" className="pr-3">
          <img src="/crs-logo.png" alt="CRS Logo" className="mr-2 h-5 w-8" />
          CRS Data
          <LucideChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        className="flex max-w-48 flex-col items-start gap-1 p-0">
        <CrsDataParcelDialog listing={listing} />

        {links.map((link) => {
          return (
            <Button
              key={link.url}
              asChild
              variant="ghost"
              size="sm"
              className="flex w-full justify-start gap-2 rounded-b-none">
              <a href={link.url} target="_blank" rel="noreferrer">
                <link.icon className="h-4 w-4" />
                {link.label}
              </a>
            </Button>
          )
        })}
      </Popover.Content>
    </Popover>
  )
}

function CrsDataParcelDialog({ listing }: { listing: Listing }) {
  const [isOpen, setIsOpen] = useState(false)

  // listing.property.address if no address, show warning until lat/lng in place

  const mutation = useMutation<CrsDataRecord>({
    mutationFn: async () => {
      const response = await fetch('/api/partners/crs-data', {
        method: 'POST',
        body: JSON.stringify({ property: listing.property }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const result = await response.json()
      return result.data
    },
    onError: (error) => {
      console.error('Error fetching property record:', error)
    },
  })

  useEffect(() => {
    if (isOpen && !mutation.data && mutation.isIdle) {
      mutation.mutate()
    }
  }, [isOpen, mutation])

  const renderContent = useCallback(() => {
    if (mutation.isPending) {
      return (
        <div className="my-10 flex flex-col items-center justify-center gap-4">
          <LoadingCircle className="h-10 w-10 text-primary" />
          <p className="">Loading CRS Property Record...</p>
        </div>
      )
    } else if (mutation.isError) {
      return (
        <p className="my-10 text-center text-red-500">
          Error fetching CRS Property Record. Please try again later or contact
          support via Intercom.
        </p>
      )
    } else if (mutation.data?.propertiesFound === 0) {
      return (
        <p className="italic text-muted-foreground">
          No CRS Data record returned for address{' '}
          {fmtAddress(listing.property, {
            oneLine: true,
            locationVisibility: listing.locationVisibility,
          })}
          . Please send this listing to support so we can investigate.
        </p>
      )
    } else if (mutation.data?.property) {
      return <RecordPropertyAccordion recordProperty={mutation.data.property} />
    }
    return null
  }, [mutation])

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex w-full justify-start gap-2 rounded-b-none">
          <LucideSquareMousePointer className="h-4 w-4" />
          Parcel & Tax Record
        </Button>
      </Dialog.Trigger>
      <Dialog.Content className="flex max-h-dvh  max-w-lg flex-col sm:max-h-[540px]">
        <Dialog.Header className="mb-2">
          <Dialog.Title>CRS Property Record</Dialog.Title>
          <Dialog.Description className="sr-only">
            CRS Property Record
          </Dialog.Description>
        </Dialog.Header>
        <div className="flex-1 overflow-y-auto">{renderContent()}</div>
        <p className="text-xs italic text-muted-foreground">
          This information is provided by CRS Data and is not maintained by
          Resimplifi
        </p>
      </Dialog.Content>
    </Dialog>
  )
}

function RecordPropertyAccordion({
  recordProperty,
}: {
  recordProperty: CrsDataProperty
}) {
  const [accordionValue, setAccordionValue] = useState<string[]>(['property'])

  const otherKeys = [
    'salesHistory',
    'taxAssessment',
    'taxHistory',
    'mortgages',
    'foreclosures',
    'improvements',
    'extraFeatures',
    'lotInformation',
    'utilities',
    'legal',
    'floodZones',
  ]

  const { propertyFields, otherFields } = useMemo(() => {
    const propertyFields: Record<string, any> = {}
    const otherFields: Record<string, any> = {}

    Object.entries(recordProperty).forEach(([key, value]) => {
      if (otherKeys.includes(key)) {
        otherFields[key] = value
      } else {
        propertyFields[key] = value
      }
    })
    return { propertyFields, otherFields }
  }, [recordProperty])

  return (
    <Accordion
      type="multiple"
      value={accordionValue}
      onValueChange={setAccordionValue}>
      <Accordion.Item value="property">
        <Accordion.Trigger className="flex w-full justify-between text-lg font-medium">
          Property Information
        </Accordion.Trigger>
        <Accordion.Content>
          <dl className="grid grid-cols-12 pb-4 text-base">
            {Object.entries(propertyFields).map(([key, value], index) => {
              return <RecordField key={key} fieldKey={key} value={value} />
            })}
          </dl>
        </Accordion.Content>
      </Accordion.Item>
      {otherKeys.map((key) => {
        if (!otherFields[key]) {
          return null
        }
        const otherFieldsValue =
          Array.isArray(otherFields[key]) && otherFields[key].length === 1
            ? otherFields[key][0]
            : otherFields[key]

        return (
          <Accordion.Item key={key} value={key}>
            <Accordion.Trigger className="flex w-full justify-between text-lg font-medium">
              {camelCaseToWords(key)}
            </Accordion.Trigger>
            <Accordion.Content>
              <dl className="grid grid-cols-12 pb-4 text-base">
                {Array.isArray(otherFieldsValue) ? (
                  <>
                    {otherFieldsValue.length === 0 ? (
                      <p className="col-span-12 italic text-muted-foreground">
                        No {camelCaseToWords(key)} data returned
                      </p>
                    ) : (
                      <RecordField fieldKey={null} value={otherFieldsValue} />
                    )}
                  </>
                ) : (
                  <>
                    {Object.entries(otherFieldsValue).map(([k, value]) => {
                      return (
                        <RecordField
                          key={k}
                          fieldKey={k}
                          value={value as any}
                        />
                      )
                    })}
                  </>
                )}
              </dl>
            </Accordion.Content>
          </Accordion.Item>
        )
      })}
    </Accordion>
  )
}

const labelOverrides: Record<string, string> = {
  taxID: 'Tax ID',
  formattedTaxID: 'Formatted Tax ID',
  alternateTaxID: 'Alternate Tax ID',
  historicalTaxID: 'Historical Tax ID',
  censusFips: 'Census FIPS',
  mlsInfo: 'MLS Info',
  mlsArea: 'MLS Area',
  mlsSubArea: 'MLS Sub Area',
  bfe: 'BFE',
  ssdTaxes: 'SSD Taxes',
}

const excludeKeys = ['upi']

function RecordField({
  fieldKey,
  value,
  index,
  labelClassName = 'col-span-5',
  valueClassName = 'col-span-7 text-right',
}: {
  fieldKey: string | null
  value: string | string[] | Record<string, any>
  index?: number
  labelClassName?: string
  valueClassName?: string
}) {
  if (excludeKeys.includes(fieldKey || '')) {
    return null
  }

  const label = fieldKey
    ? labelOverrides[fieldKey] || camelCaseToWords(fieldKey)
    : fieldKey
  const isObject = typeof value === 'object' && value !== null
  const isArray = Array.isArray(value)

  const renderValue = useCallback((value: any) => {
    if (value === null || typeof value === 'undefined' || value === '') {
      return '--'
    }
    return value
  }, [])

  if (isArray) {
    if (
      value.length === 0 ||
      typeof value[0] === 'string' ||
      typeof value[0] === 'number'
    ) {
      return <RecordField fieldKey={fieldKey} value={value.join(', ')} />
    }
    return (
      <>
        {label && (
          <dt className="col-span-12 py-1 font-medium text-muted-foreground">
            {label}
          </dt>
        )}
        <dd className="col-span-12 py-1 font-semibold">
          {value.map((value, index) => {
            return (
              <RecordField
                key={index}
                fieldKey={null}
                value={value}
                index={index}
              />
            )
          })}
        </dd>
      </>
    )
  } else if (isObject) {
    return (
      <>
        {label && (
          <dt className="col-span-12 py-1 font-medium text-muted-foreground">
            {label}
          </dt>
        )}
        <dd
          className={cn(
            'col-span-12 mb-2 grid grid-cols-12 flex-col rounded-md px-3 py-2 font-semibold',
            !index && 'bg-muted/50',
            index && index % 2 === 0 ? 'bg-muted/50' : 'mb-0'
          )}>
          {Object.entries(value).map(([key, val]) => {
            return <RecordField fieldKey={key} value={val} />
          })}
        </dd>
      </>
    )
  }
  return (
    <>
      <dt
        className={cn(
          'py-1 font-medium text-muted-foreground',
          labelClassName
        )}>
        {label}
      </dt>
      <dd className={cn('py-1 font-semibold', valueClassName)}>
        {renderValue(value)}
      </dd>
    </>
  )
}

type CrsDataRecord = {
  version: string
  propertiesFound: number
  property?: CrsDataProperty
}

type CrsDataProperty = {
  ownerName: string
  UPI: string
  taxID: string
  formattedTaxID: string
  specialInterest: string
  alternateTaxID: string
  historicalTaxID: string[]
  municipality: string
  county: string
  state: string
  censusFips: string
  accountNumber: string
  carrierRoute: string
  township: string
  range: string
  section: string
  mapCode: string
  address: CrsDataAddress
  mailingAddress: CrsDataAddress
  propertyType: string
  improvementType: string
  landUse: string
  censusTract: string
  censusBlock: string
  coordinates: number[]
  squareFeet: number
  assessorRollYear: string
  salesHistory: CrsDataSale[]
  taxAssessment: CrsDataTaxAssessment
  taxHistory: CrsDataTaxHistory[]
  mortgages: CrsDataMortgage[]
  foreclosures: CrsDataForeclosure[]
  improvements: CrsDataImprovement[]
  extraFeatures: CrsDataExtraFeature[]
  lotInformation: CrsDataLotInfo[]
  utilities: CrsDataUtility[]
  legal: CrsDataLegal[]
  floodZones: CrsDataFloodZone[]
}

type CrsDataAddress = {
  full: string
  house: string
  streetPre: string
  streetName: string
  streetSuffix: string
  streetSuffixExpanded: string
  streetPost: string
  unit: string
  city: string
  state: string
  country: string
  zip: string
  zip4: string
}

type CrsDataSale = {
  saleDate: string
  saleRecordedDate: string
  salePrice: number
  buyer: string
  instrument: string
  book: string
  page: string
  documentNumber: string
  numberParcels: number
}

type CrsDataTaxAssessment = {
  appraisalYear: string
  appraisedImprovementValue: number
  appraisedLandValue: number
  totalAppraisedValue: number
  assessmentYear: string
  assessedImprovementValue: number
  assessedLandValue: number
  totalAssessedValue: number
  totalTaxes: number
  taxYear: string
  isPreliminaryValues: boolean
  exemptionAmount: number
  exemptionReason: string
  percentImproved: number
  taxAssessmentHistory: {
    year: string
    assessedLand: number
    assessedImprovements: number
    totalAssessment: number
  }[]
  taxDetails: {
    taxEntity: string
    taxRate: number
    taxAmount: number
  }[]
  jurisdictions: {
    label: string
    rate: string
  }[]
}

type CrsDataTaxHistory = {
  taxYear: string
  cityTaxes: number
  countyTaxes: number
  totalTaxes: number
}

type CrsDataMortgage = {
  loanDate: string
  loanRecordedDate: string
  loanAmount: number
  borrower: string
  borrower2: string
  lenderName: string
  book: string
  page: string
  documentNumber: string
}

type CrsDataForeclosure = {
  filingDate: string
  auctionDate: string
  defendant: string
  plaintiff: string
  status: string
  caseNumber: string
  book: string
  page: string
  documentNumber: string
}

type CrsDataImprovement = {
  buildingNumber: string
  improvementType: string
  condition: string
  yearBuilt: string
  effectiveYear: string
  stories: string
  units: string
  totalRooms: number
  bedrooms: number
  bathrooms: number
  halfBathrooms: number
  totalSqFt: number
  grossSqFt: number
  quality: string
  roofFraming: string
  shape: string
  roofCover: string
  partitions: number
  cabinetMillwork: string
  constructionStyle: string
  constructionType: string
  commonWall: string
  floorFinish: string
  foundation: string
  interiorFinish: string
  floorSystem: string
  airConditioning: string
  exteriorWall: string
  heating: string
  heatingFuel: string
  structuralFraming: string
  bathroomTile: string
  firePlace: string
  firePlaceCount: number
  plumbingFixtures: number
  livingSpaceSqFt: {
    type: string
    squareFeet: number
  }[]
  otherSqft: {
    type: string
    squareFeet: number
  }[]
}

type CrsDataExtraFeature = {
  feature: string
  size: string
  yearBuilt: string
  condition: string
}

type CrsDataLotInfo = {
  specialSchoolDistrict1: string
  specialSchoolDistrict2: string
  dimensions: string
  acreage: number
  calculatedAcreage: number
  lotSquareFeet: number
  ownerType: string
  roadType: string
  topography: string
  districtTrend: string
  zoning: string[]
}

type CrsDataUtility = {
  gasSource: string
  sewerSource: string
  waterSource: string
  electricSource: string
}

type CrsDataLegal = {
  section: string
  lot: string
  block: string
  subdivision: string
  legalDescription: string
  platBook: string
  platPage: string
  districtWard: string
}

type CrsDataFloodZone = {
  floodZoneCode: string
  risk: string
  bfe: string
  firmPanel: string
}

const SAMPLE_DATA: CrsDataRecord = {
  version: '1',
  propertiesFound: 1,
  property: {
    ownerName: 'Davis Hubert Lamson',
    upi: 'US-22033-N-3270254-R-N',
    taxID: '3270254',
    formattedTaxID: '3270254',
    alternateTaxID: '',
    historicalTaxID: ['03270254'],
    county: 'East Baton Rouge',
    state: 'LA',
    censusFips: '22033',
    accountNumber: '',
    carrierRoute: 'C038',
    // @ts-expect-error
    address: {
      full: '2829 Needham Dr Baton Rouge LA 70814-4128',
      house: '2829',
      streetPre: '',
      streetName: 'Needham',
      streetSuffix: 'DR',
      streetSuffixExpanded: 'Drive',
      streetPost: '',
      unit: '',
      city: 'Baton Rouge',
      state: 'LA',
      zip: '70814',
      zip4: '4128',
    },
    mailingAddress: {
      full: '1877 Brocade Dr Baton Rouge LA 70815-4804',
      house: '1877',
      streetPre: '',
      streetName: 'Brocade',
      streetSuffix: 'DR',
      streetSuffixExpanded: 'Drive',
      streetPost: '',
      unit: '',
      city: 'Baton Rouge',
      state: 'LA',
      country: '',
      zip: '70815',
      zip4: '4804',
    },
    propertyType: 'Commercial',
    improvementType: 'Store/Office Mixed Use',
    landUse: 'Store/Office Mixed Use',
    censusTract: '35.06',
    censusBlock: '5',
    coordinates: [30.47467462, -91.07992335],
    squareFeet: 0,
    assessorRollYear: '2024',
    salesHistory: [
      {
        saleDate: '10/19/2021',
        saleRecordedDate: '11/12/2021',
        salePrice: 48000,
        buyer: 'Davis Hubert Lamson',
        // @ts-expect-error
        buyer2: '',
        seller: 'Davis III John Francis & Davis Ross Edwin',
        instrument: 'Cash Sale Deed',
        quality: '',
        book: '',
        page: '',
        documentNumber: '13148314',
        numberParcels: 1,
      },
    ],
    // @ts-expect-error
    taxAssessment: {
      appraisalYear: '2024',
      appraisedImprovementValue: 64240,
      appraisedLandValue: 24310,
      totalAppraisedValue: 88550,
      assessmentYear: '2024',
      assessedImprovementValue: 9636,
      totalAssessedValue: 12067,
      assessedLandValue: 2431,
      totalTaxes: 1413.33,
      taxYear: '2024',
      isPreliminaryValues: false,
      exemptionAmount: 0,
      exemptionReason: '',
      taxAssessmentHistory: [
        {
          year: '2024',
          assessedLand: 2431,
          assessedImprovements: 9636,
          totalAssessment: 12067,
        },
        {
          year: '2023',
          assessedLand: 2210,
          assessedImprovements: 8760,
          totalAssessment: 10970,
        },
        {
          year: '2022',
          assessedLand: 2210,
          assessedImprovements: 8760,
          totalAssessment: 10970,
        },
      ],
    },
    taxHistory: [
      {
        taxYear: '2024',
        cityTaxes: 0,
        countyTaxes: 0,
        // @ts-expect-error
        ssdTaxes: 0,
        totalTaxes: 1413.33,
      },
      {
        taxYear: '2023',
        cityTaxes: 0,
        countyTaxes: 0,
        // @ts-expect-error
        ssdTaxes: 0,
        totalTaxes: 1306.8,
      },
      {
        taxYear: '2022',
        cityTaxes: 0,
        countyTaxes: 0,
        // @ts-expect-error
        ssdTaxes: 0,
        totalTaxes: 1309.85,
      },
    ],
    mortgages: [],
    improvements: [
      {
        buildingNumber: '1',
        improvementType: 'Store/Office Mixed Use',
        condition: 'Good',
        yearBuilt: '',
        effectiveYear: '',
        stories: '',
        units: '',
        totalRooms: 0,
        bedrooms: 0,
        bathrooms: 0,
        halfBathrooms: 0,
        totalSqFt: 0,
        quality: '',
        roofFraming: '',
        shape: '',
        roofCover: '',
        partitions: 0,
        cabinetMillwork: '',
        constructionStyle: '',
        constructionType: '',
        commonWall: '',
        floorFinish: '',
        foundation: '',
        interiorFinish: '',
        floorSystem: '',
        airConditioning: '',
        exteriorWall: '',
        heating: '',
        structuralFraming: '',
        bathroomTile: '',
        firePlace: '',
        firePlaceCount: 0,
        livingSpaceSqFt: [],
        // @ts-expect-error
        otherSqFt: [],
      },
    ],
    extraFeatures: [],
    lotInformation: {
      // @ts-expect-error
      specialSchoolDistrict1: 'E B R Sch',
      specialSchoolDistrict2: '',
      dimensions: '',
      acreage: 0,
      lotSquareFeet: 0,
      ownerType: '',
      roadType: '',
      topography: '',
      districtTrend: '',
      zoning: [''],
    },
    utilities: {
      // @ts-expect-error
      gasSource: '',
      sewerSource: '',
      waterSource: '',
      electricSource: '',
    },
    legal: {
      // @ts-expect-error
      lot: '195',
      block: '',
      subdivision: 'South Park Forest',
      legalDescription:
        'Ward: 1-4, Subdivision: South Park Forest, Lot: 195. (Commercial Office & Warehouse)4th Filing, Part 2',
      platBook: '',
      platPage: '',
      districtWard: '99033',
    },
    floodZones: [
      {
        floodZoneCode: 'X',
        risk: 'Minimal',
        bfe: '',
        // @ts-expect-error
        description:
          'Area of minimal flood hazard, usually depicted on FIRMs as above the 500-year flood level.',
        firmPanel: '22033C0255F',
        effectiveDate: '06/19/2012',
      },
    ],
    mlsInfo: {
      mlsArea: '41',
      mlsSubArea: '',
    },
  },
}
